import { useEffect, useState } from "react";
import { DrawTransport } from "./DrawTransport";
import type { DrawClient, DrawJoinOpts, DrawRoomState } from "./types";

/** Connect to a DrawIt room for the lifetime of the page. `opts` must be
 *  memoised (stable code/role/id) or the socket reconnects every render. */
export function useDrawRoom(opts: DrawJoinOpts | null): {
  client: DrawClient | null;
  state: DrawRoomState | null;
  error: string | null;
} {
  const [client, setClient] = useState<DrawClient | null>(null);
  const [state, setState] = useState<DrawRoomState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opts) return;
    const c = new DrawTransport();
    setClient(c);
    const off = c.on((e) => {
      if (e.type === "state") {
        setError(null);
        setState((prev) => {
          const nr = e.state.rev, cr = prev?.rev;
          if (typeof nr === "number" && typeof cr === "number" && nr <= cr) return prev;
          return e.state;
        });
      } else if (e.type === "error") {
        setError(e.message);
      }
    });
    c.join(opts).then(setState).catch((err) => setError((err as Error).message ?? "Could not join DrawIt"));
    return () => { off(); c.leave(); setClient(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts?.code, opts?.role, opts?.me.id]);

  return { client, state, error };
}
